import { MUISelect, MUITextField } from "@abb/arcadia-ui-react"
import { DropdownLOVType } from "@abb/arcadia-ui-react/build/components/select/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useContext, useEffect, useMemo } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { z } from "zod"
import { useDeviceFamilies } from "../../../../../hooks/useDeviceFamilies"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import { DiMaProgress } from "../../../../components/DiMaProgress"
import { UserContext } from "../../../../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../../models/Authorization"
import { DeviceGet, DevicePut } from "../../../../models/Device"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  slaveId: z
    .number({ error: "Slave ID must be a number" })
    .int("Slave ID must be an integer")
    .min(0, "Slave ID is required"),
  familyId: z.string().min(1, "Device Family is required"),
})

const returnURL = `/devices`

export function DeviceDetails(props: {
  readonly device: DeviceGet
  readonly setDevice: (updatedDevice: DeviceGet) => void
}) {
  const { user, isLoadingUser } = useContext(UserContext)
  const { device, setDevice } = props
  const { deviceFamiliesList, isLoadingDeviceFamilies } = useDeviceFamilies()

  const formMethods = useForm<DevicePut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = formMethods

  useEffect(() => {
    reset({
      name: device.name,
      slaveId: device.slaveId,
      familyId: device.family.id,
    })
  }, [device, reset])

  const onSubmit = useCallback(
    (values: DevicePut) =>
      digitalManagerApi
        .put<DeviceGet>(`/devices/${device.id}`, values)
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            toast.success("Device updated")
            setDevice(res.data)
          }
        })
        .catch(() => {
          toast.error("Cannot update device")
          throw new Error("Cannot update device")
        }),
    [device.id, setDevice]
  )

  const canWrite = useMemo(
    () =>
      user
        ? hasUserAccessToFeature(
            user.authorizations,
            DimaFeatureShortNameEnum.DM,
            AccessLevel.Write
          )
        : false,
    [user]
  )

  const handleNameFieldChange = useCallback(
    (field: ControllerRenderProps<DevicePut, "name">) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderNameTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<DevicePut, "name"> }) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleNameFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={!canWrite}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [canWrite, errors.name, handleNameFieldChange]
  )

  const handleChangeNumberField = useCallback(
    (value: string) => {
      const parsedValue = value ? Number.parseInt(value, 10) : 0
      setValue("slaveId", parsedValue, { shouldValidate: true, shouldDirty: true })
    },
    [setValue]
  )

  const renderSlaveIdTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<DevicePut, "slaveId"> }) => (
      <MUITextField
        id="slaveId"
        type="number"
        size={32}
        name={field.name}
        value={
          field.value !== undefined && field.value !== null ? String(field.value) : ""
        }
        onChange={handleChangeNumberField}
        onBlur={field.onBlur}
        isDisabled={!canWrite}
        errors={
          typeof errors.slaveId?.message === "string"
            ? { slaveId: { message: errors.slaveId.message } }
            : undefined
        }
      />
    ),
    [canWrite, errors.slaveId, handleChangeNumberField]
  )

  const handleFamilySelectChange = useCallback(
    (field: ControllerRenderProps<DevicePut, "familyId">) =>
      (values: string[] | DropdownLOVType[]) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        field.onChange(value)
      },
    []
  )

  const renderFamilyIdSelect = useCallback(
    ({ field }: { field: ControllerRenderProps<DevicePut, "familyId"> }) => (
      <MUISelect
        name={field.name}
        id="familyId"
        size={32}
        dropdownLOV={deviceFamiliesList.map((d) => ({
          id: String(d.id),
          name: d.name ?? "",
        }))}
        selectedItem={field.value ? [String(field.value)] : []}
        onChange={handleFamilySelectChange(field)}
        onBlur={field.onBlur}
        isDisabled={isLoadingDeviceFamilies || !canWrite}
        errors={
          errors.familyId?.message
            ? { familyId: { message: String(errors.familyId.message) } }
            : undefined
        }
      />
    ),
    [
      deviceFamiliesList,
      errors.familyId,
      isLoadingDeviceFamilies,
      canWrite,
      handleFamilySelectChange,
    ]
  )

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsContentProps: DiMaDetailsContentProps<DevicePut> = {
    formMethods,
    canWrite,
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Name",
        displayItem: <div style={{ width: "100%" }}>{device.name}</div>,
        editItem: (
          <Controller name="name" control={control} render={renderNameTextField} />
        ),
      },
      {
        id: 1,
        label: "Slave ID",
        displayItem: <div style={{ width: "100%" }}>{device.slaveId}</div>,
        editItem: (
          <Controller name="slaveId" control={control} render={renderSlaveIdTextField} />
        ),
      },
      {
        id: 2,
        label: "Family",
        displayItem: <div style={{ width: "100%" }}>{device.family.name}</div>,
        editItem: (
          <Controller name="familyId" control={control} render={renderFamilyIdSelect} />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
